<script setup lang="ts">
import { GameState, useGameState } from '../stores/gameState'
import { storeToRefs } from 'pinia'

import { useInHandActions } from '@/composables/useInHandActions'
import { SuggestedActionQuality } from '@acepoker/api-core'
import BestSvg from '@/assets/icons/statistic/best.svg'
import CorrectSvg from '@/assets/icons/statistic/correct.svg'
import InaccuracySvg from '@/assets/icons/statistic/inaccuracies.svg'
import WrongSvg from '@/assets/icons/statistic/wrong.svg'
import BlunderSvg from '@/assets/icons/statistic/blunders.svg'
import type { SuggestedAction } from '@acepoker/contract'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  actions: SuggestedAction[]
  size?: 'small' | 'large'
  lastActionNameRaw?: string
  isReviewMode?: boolean
  isHintMode?: boolean
}>()

const emit = defineEmits(['buttonClick'])

type SuggestedActionExtended = SuggestedAction & {
  actionClassName: string
  label: string
  suffix?: string
  fn: () => void
}

const { bet, fold, call, check, raise, isPending } = useInHandActions()

const { gameState, roomState, showButtons } = storeToRefs(useGameState())

const ACTIONS_ORDER = ['f', 'c', 'x', 'r', 'allin']

const getActionShortcut = (actionNameRaw: string) => {
  if (actionNameRaw.startsWith('allin')) return 'allin'
  if (actionNameRaw.startsWith('r') || actionNameRaw.startsWith('b')) return 'r'
  return actionNameRaw
}

const prepareSuggestedActionData = (suggestedActions: SuggestedAction[]) => {
  const sorted = suggestedActions
    .sort(({ amount: a }, { amount: b }) => {
      if (!a || !b) {
        return 0
      }
      return a - b
    })
    .sort(({ actionNameRaw: a }, { actionNameRaw: b }) => {
      const aShortcut = getActionShortcut(a)
      const bShortcut = getActionShortcut(b)
      const aIndex = ACTIONS_ORDER.indexOf(aShortcut)
      const bIndex = ACTIONS_ORDER.indexOf(bShortcut)
      return aIndex - bIndex
    })
  const result: SuggestedActionExtended[] = []

  let rIndex = 1
  for (const action of sorted) {
    const { actionNameRaw, amount } = action
    if (actionNameRaw === 'c') {
      result.push({
        ...action,
        actionClassName: 'c',
        label: 'label.Call',
        fn: () => call()
      })
    }
    if (actionNameRaw === 'f') {
      result.push({
        ...action,
        actionClassName: 'f',
        label: 'label.Fold',
        fn: () => fold()
      })
    }
    if (actionNameRaw === 'x') {
      result.push({
        ...action,
        actionClassName: 'c',
        label: 'label.Check',
        fn: () => check()
      })
    }
    if (actionNameRaw.startsWith('allin') && amount) {
      result.push({
        ...action,
        actionClassName: 'allin',
        label: 'label.AllIn',
        fn: () => raise(amount)
      })
    }

    const MAX_ACTIONS_COLOR_LEVEL = 5
    if (actionNameRaw.startsWith('r') && amount) {
      result.push({
        ...action,
        actionClassName: 'r' + Math.min(rIndex++, MAX_ACTIONS_COLOR_LEVEL),
        label: 'label.Raise',
        suffix: `${actionNameRaw.replace(/\D/g, '')}%`,
        fn: () => raise(amount)
      })
    }
    if (actionNameRaw.startsWith('b') && amount) {
      result.push({
        ...action,
        actionClassName: 'r' + Math.min(rIndex++, MAX_ACTIONS_COLOR_LEVEL),
        label: 'label.Bet',
        suffix: `${actionNameRaw.replace(/\D/g, '')}%`,
        fn: () => bet(amount)
      })
    }
  }
  return result
}

const onButtonClick = async (action: SuggestedActionExtended) => {
  if (isPending.value) return

  emit('buttonClick', action)
}
</script>

<template>
  <div
    v-if="showButtons"
    :class="{
      'suggested-actions': true,
      'suggested-actions--away':
        gameState === GameState.ERROR ||
        gameState === GameState.DISCONNECTED ||
        gameState === GameState.INIT,
      'suggested-actions__1': props.actions.length == 1,
      'suggested-actions__2': props.actions.length == 2
    }"
  >
    <div
      :key="action.actionNameRaw"
      v-for="action of prepareSuggestedActionData(props.actions)"
      :class="[
        'suggested-actions-button',
        `suggested-actions-button-${action.actionClassName}`,
        {
          'suggested-actions-button-unselected':
            props.isReviewMode && props.lastActionNameRaw !== action.actionNameRaw,
          [`suggested-actions-button__${props.size}`]: !!props.size
        }
      ]"
      @click="() => onButtonClick(action)"
    >
      <best-svg
        class="suggested-actions-icon"
        v-if="props.isHintMode && action.quality === SuggestedActionQuality.Best"
      />
      <correct-svg
        class="suggested-actions-icon"
        v-if="props.isHintMode && action.quality === SuggestedActionQuality.Correct"
      />
      <inaccuracy-svg
        class="suggested-actions-icon"
        v-if="props.isHintMode && action.quality === SuggestedActionQuality.Inaccuracy"
      />
      <wrong-svg
        class="suggested-actions-icon"
        v-if="props.isHintMode && action.quality === SuggestedActionQuality.Wrong"
      />
      <blunder-svg
        class="suggested-actions-icon"
        v-if="props.isHintMode && action.quality === SuggestedActionQuality.Blunder"
      />
      <div class="suggested-actions-button-box">
        <div
          :class="[
            'suggested-actions-button-label',
            {
              'suggested-actions-button-label__review-size': props.isReviewMode,
              'suggested-actions-button-label__review-color':
                props.isReviewMode && props.lastActionNameRaw !== action.actionNameRaw
            }
          ]"
        >
          {{ t(action.label) }} {{ action.suffix }}
        </div>
        <transition name="suggested-actions-button-data-box">
          <div class="suggested-actions-button-data-box" v-if="props.isHintMode">
            <div class="suggested-actions-button-strategy">
              {{ (action.strategy * 100).toFixed(1) }} %
            </div>
            <div class="suggested-actions-button-ev">
              {{ action.ev.toFixed(1) }}
              {{ $t('SuggestedActions.label.EV') }}
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.suggested-actions {
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 5px;
  height: 93px;
  user-select: none;

  &__1 {
    grid-template-columns: 1fr;
  }

  &__2 {
    grid-template-columns: 1fr 1fr;
  }

  &--away {
    filter: grayscale(100%);
  }

  &-button {
    min-width: 100px;
    height: 44px;
    border-radius: 48px;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.41);
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    transition: all 0.2s ease-out;
    user-select: none;
    cursor: pointer;

    &__large {
      min-width: 140px !important;
      height: 64px !important;

      .suggested-actions-button-label {
        font-size: 18px;

        &__review-size {
          font-size: 16px;
        }
      }
    }

    &-unselected {
      background: #303037 !important;
    }

    &-r1 {
      background: #2789d3;
    }

    &-r2 {
      background: #2373ba;
    }

    &-r3 {
      background: #1c5ba1;
    }

    &-r4 {
      background: #1a4486;
    }

    &-r5 {
      background: #0e2e61;
    }

    &-allin {
      background: #0a2755;
    }

    &-f {
      background: #a93939;
    }

    &-c {
      background: #279a33;
    }

    &-box {
      display: flex;
      flex-direction: column;
      transition: all 0.2s ease-out;
    }

    &-label {
      text-align: center;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      color: white;
      text-shadow: 0px 1px 0px rgba(0, 0, 0, 0.4);
      transition: all 0.2s ease-out;

      &__review-size {
        font-size: 10px;
      }

      &__review-color {
        color: #b5b4ba;
      }
    }

    &-data-box {
      display: flex;
      gap: 8px;
      justify-content: space-between;
      color: #fff;
      text-align: center;
      font-family: Poppins;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;

      &-enter-active,
      &-leave-active {
        transition: all 0.2s ease-out;
      }

      &-enter-from,
      &-leave-to {
        max-height: 0;
        opacity: 0;
      }
    }
  }

  &-icon {
    position: absolute;
    width: 20px;
    height: 20px;
    right: 0px;
    top: -8px;
  }
}
</style>
